import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { classNames } from 'primereact/utils';
import { RegisterPolicyHolder, UserIdAlreadyExists } from '../services/services';

const RegisterHolder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState({ active: false, message: '', error: false, status: 200 });
  const [userIdExists, setUserIdExists] = useState(false);

  const { control, handleSubmit, watch, formState: { errors }, setError: setFormError, clearErrors } = useForm({
    defaultValues: {
      userId: '',
      password: '',
      confirmation: '',
      firstName: '',
      lastName: '',
      emailAddr: '',
      confirmEmailAddr: ''
    }
  });

  const watchedUserId = watch('userId');
  const watchedPassword = watch('password');
  const watchedEmailAddr = watch('emailAddr');

  // Check if we have valid policy validation data
  const policyValidation = location.state?.policyValidation;
  
  useEffect(() => {
    if (!policyValidation || !Object.keys(policyValidation).length) {
      navigate('/');
    }
  }, [policyValidation, navigate]);

  // Check if username already exists
  useEffect(() => {
    const checkUserIdExists = async () => {
      if (watchedUserId && watchedUserId.length >= 4) {
        try {
          const response = await UserIdAlreadyExists({ userId: watchedUserId });
          if (typeof response.result !== 'undefined' && response.result === true) {
            setUserIdExists(true);
            setFormError('userId', {
              type: 'manual',
              message: 'The Username provided has already been used. Please select another'
            });
          } else {
            setUserIdExists(false);
            clearErrors('userId');
          }
        } catch (error) {
          console.error('Error checking username:', error);
        }
      }
    };

    const timeoutId = setTimeout(checkUserIdExists, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [watchedUserId, setFormError, clearErrors]);

  const onSubmit = async (data) => {
    setFormLoading(true);
    setError({ active: false, message: '', error: false, status: 200 });

    try {
      const policyHolderRegistrationInfo = {
        authCodeValidation: { ...policyValidation },
        userRegistry: {
          userId: data.userId,
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddr: data.emailAddr,
          password: data.password
        }
      };

      const policyholderRegistry = await RegisterPolicyHolder(policyHolderRegistrationInfo);
      
      navigate('/regcomplete', {
        state: { policyholderRegistry }
      });
    } catch (error) {
      setError({
        active: true,
        message: error.message || 'Registration failed. Please try again.',
        error: error.error || 'Registration Error',
        status: error.status || 500
      });
    } finally {
      setFormLoading(false);
    }
  };

  const onCancel = () => {
    navigate('/');
  };

  const formatUserId = (value) => {
    return value.replace(/(\s)|(')|(")|(,)/g, '');
  };

  const passwordValidation = {
    required: 'Password is required',
    minLength: { value: 8, message: 'Password must be at least 8 characters' },
    maxLength: { value: 24, message: 'Password must be at most 24 characters' },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
      message: 'Password must contain at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character'
    }
  };

  const passwordRequirements = (
    <Panel header="Password Requirements" className="mb-3">
      <div style={{ fontSize: '0.9rem' }}>
        <div className="mb-2"><strong>Password Tips:</strong></div>
        <div className="mb-1">
          <i className="pi pi-check-circle text-green-500 mr-2"></i>
          Password must be at least <strong>8 characters long</strong>.
        </div>
        <div className="mb-1">
          <i className="pi pi-check-circle text-green-500 mr-2"></i>
          Must include at least <strong>one uppercase letter</strong>.
        </div>
        <div className="mb-1">
          <i className="pi pi-check-circle text-green-500 mr-2"></i>
          Must include at least <strong>one lowercase letter</strong>.
        </div>
        <div className="mb-1">
          <i className="pi pi-check-circle text-green-500 mr-2"></i>
          Must include at least <strong>one number</strong>.
        </div>
        <div className="mb-1">
          <i className="pi pi-check-circle text-green-500 mr-2"></i>
          Must include at least <strong>one special character</strong> (@, %, &, #).
        </div>
      </div>
    </Panel>
  );

  const bestPractices = (
    <Panel header="Best Practices for Stronger Passwords">
      <div style={{ fontSize: '0.9rem' }}>
        <div className="mb-2"><strong>Use:</strong></div>
        <ul className="list-disc ml-4 mb-3">
          <li>A <strong>unique</strong> password not used anywhere else.</li>
          <li><strong>UPPER</strong> and <strong>lower case</strong> letters along with numbers and any of the following special characters (@,%,&,#)</li>
        </ul>
        
        <div className="mb-2"><strong>Don't use:</strong></div>
        <ul className="list-disc ml-4 mb-3">
          <li>Your <strong>username</strong> or <strong>email address.</strong></li>
          <li>Personal information (your name, phone number, etc.).</li>
          <li>Repeating patterns (aaaa, 1111, 1234, abcd)</li>
        </ul>
        
        <div className="mb-2"><strong>Avoid:</strong></div>
        <ul className="list-disc ml-4">
          <li>Writing down your password.</li>
          <li>Sharing your password with anyone.</li>
          <li>Saving your password in a browser.</li>
        </ul>
      </div>
    </Panel>
  );

  if (!policyValidation) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="container">
      <div className="grid">
        <div className="col-12">
          <p>Please provide the following information so that we may register you as a user.</p>
        </div>
      </div>
      <Card>
        {error.active && (
          <Message 
            severity="error" 
            text={error.message}
            style={{ marginBottom: '1rem' }}
          />
        )}
        <div className="grid">
          {/* Form fields column */}
          <div className="col-6" style={{width: '80%'}}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-fields-column">
                <div className="field">
                  <label htmlFor="userId" className="block">A username of your choosing:</label>
                  <Controller
                    name="userId"
                    control={control}
                    rules={{ 
                      required: 'Username is required',
                      minLength: { value: 4, message: 'Username must be at least 4 characters' },
                      maxLength: { value: 64, message: 'Username must be at most 64 characters' }
                    }}
                    render={({ field }) => (
                      <InputText
                        id="userId"
                        {...field}
                        onChange={(e) => field.onChange(formatUserId(e.target.value))}
                        className={classNames({ 'p-invalid': errors.userId || userIdExists })}
                        disabled={formLoading}
                      />
                    )}
                  />
                  {errors.userId && <small className="p-error">{errors.userId.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="password" className="block">A password of your choice:</label>
                  <Controller
                    name="password"
                    control={control}
                    rules={passwordValidation}
                    render={({ field }) => (
                      <Password
                        id="password"
                        {...field}
                        toggleMask
                        className={classNames({ 'p-invalid': errors.password })}
                        disabled={formLoading}
                        feedback={false}
                      />
                    )}
                  />
                  {errors.password && <small className="p-error">{errors.password.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="confirmation" className="block">Please confirm your password:</label>
                  <Controller
                    name="confirmation"
                    control={control}
                    rules={{
                      required: 'Password confirmation is required',
                      validate: (value) => value === watchedPassword || 'Passwords do not match'
                    }}
                    render={({ field }) => (
                      <Password
                        id="confirmation"
                        {...field}
                        toggleMask
                        className={classNames({ 'p-invalid': errors.confirmation })}
                        disabled={formLoading}
                        feedback={false}
                      />
                    )}
                  />
                  {errors.confirmation && <small className="p-error">{errors.confirmation.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="firstName" className="block">First name:</label>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'First name is required' }}
                    render={({ field }) => (
                      <InputText
                        id="firstName"
                        {...field}
                        className={classNames({ 'p-invalid': errors.firstName })}
                        disabled={formLoading}
                      />
                    )}
                  />
                  {errors.firstName && <small className="p-error">{errors.firstName.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="lastName" className="block">Last name:</label>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Last name is required' }}
                    render={({ field }) => (
                      <InputText
                        id="lastName"
                        {...field}
                        className={classNames({ 'p-invalid': errors.lastName })}
                        disabled={formLoading}
                      />
                    )}
                  />
                  {errors.lastName && <small className="p-error">{errors.lastName.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="emailAddr" className="block">E-Mail Address:</label>
                  <Controller
                    name="emailAddr"
                    control={control}
                    rules={{ 
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    }}
                    render={({ field }) => (
                      <InputText
                        id="emailAddr"
                        {...field}
                        type="email"
                        className={classNames({ 'p-invalid': errors.emailAddr })}
                        disabled={formLoading}
                      />
                    )}
                  />
                  {errors.emailAddr && <small className="p-error">{errors.emailAddr.message}</small>}
                </div>
                <div className="field">
                  <label htmlFor="confirmEmailAddr" className="block">Confirm E-Mail Address:</label>
                  <Controller
                    name="confirmEmailAddr"
                    control={control}
                    rules={{
                      required: 'Email confirmation is required',
                      validate: (value) => value === watchedEmailAddr || 'Email addresses do not match'
                    }}
                    render={({ field }) => (
                      <InputText
                        id="confirmEmailAddr"
                        {...field}
                        type="email"
                        className={classNames({ 'p-invalid': errors.confirmEmailAddr })}
                        disabled={formLoading}
                      />
                    )}
                  />
                  {errors.confirmEmailAddr && <small className="p-error">{errors.confirmEmailAddr.message}</small>}
                </div>
                <div className="field" style={{ marginTop: '2rem' }}>
                  <div className="button-row">
                    <Button
                      type="submit"
                      label="Continue"
                      severity="warning"
                      disabled={Object.keys(errors).length > 0 || userIdExists}
                      loading={formLoading}
                    />
                    <Button
                      type="button"
                      label="Cancel"
                      severity="secondary"
                      onClick={onCancel}
                      disabled={formLoading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
          {/* Side column for password requirements and best practices */}
          <div className="col-6">
            <div className="side-panels-column">
              {passwordRequirements}
              {bestPractices}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RegisterHolder;
